/**
 * ============================================================================
 * SPOTLIGHT COMPONENT
 * ============================================================================
 *
 * A macOS-style Spotlight search overlay that allows users to search for
 * and open apps. Triggered by Cmd+S (macOS) or Ctrl+S (Windows/Linux).
 *
 * @packageDocumentation
 */

"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDesktopContext } from "../contexts/DesktopContext";
import { useThemeOptional } from "../contexts/ThemeContext";
import type { DesktopIconConfig, WindowInstance } from "../types";
import { isWindowMinimized, isWindowOpen } from "../types";

/* ==========================================================================
   TYPES
   ========================================================================== */

export interface SpotlightProps {
  /** Whether the spotlight is visible */
  isOpen: boolean;
  /** Callback to close the spotlight */
  onClose: () => void;
  /** Icon configurations to search through */
  icons: DesktopIconConfig[];
  /** Callback when an icon is clicked */
  onIconClick: (iconConfig: DesktopIconConfig) => void;
}

interface SearchResult {
  icon: DesktopIconConfig;
  windowInstance: WindowInstance | undefined;
}

/* ==========================================================================
   COMPONENT
   ========================================================================== */

export function Spotlight({
  isOpen,
  onClose,
  icons,
  onIconClick,
}: SpotlightProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const theme = useThemeOptional();
  const isDark = theme?.isDark ?? true;
  const { windows } = useDesktopContext();

  // Filter icons based on search query
  const searchResults = useMemo((): SearchResult[] => {
    if (!searchQuery.trim()) {
      // Show all apps when no search query
      return icons.map((icon) => ({
        icon,
        windowInstance: windows.get(icon.windowId),
      }));
    }

    const query = searchQuery.toLowerCase();
    return icons
      .filter((icon) => icon.label.toLowerCase().includes(query))
      .map((icon) => ({
        icon,
        windowInstance: windows.get(icon.windowId),
      }));
  }, [searchQuery, icons, windows]);

  // Reset selection when results change
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchResults.length]);

  // Focus input when spotlight opens
  useEffect(() => {
    if (isOpen) {
      setSearchQuery("");
      setSelectedIndex(0);
      // Small delay to ensure the element is rendered
      setTimeout(() => {
        inputRef.current?.focus();
      }, 50);
    }
  }, [isOpen]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case "ArrowDown":
          e.preventDefault();
          setSelectedIndex((prev) =>
            prev < searchResults.length - 1 ? prev + 1 : prev
          );
          break;
        case "ArrowUp":
          e.preventDefault();
          setSelectedIndex((prev) => (prev > 0 ? prev - 1 : prev));
          break;
        case "Enter":
          e.preventDefault();
          if (searchResults[selectedIndex]) {
            onIconClick(searchResults[selectedIndex].icon);
            onClose();
          }
          break;
        case "Escape":
          e.preventDefault();
          onClose();
          break;
      }
    },
    [searchResults, selectedIndex, onIconClick, onClose]
  );

  // Handle result click
  const handleResultClick = useCallback(
    (result: SearchResult) => {
      onIconClick(result.icon);
      onClose();
    },
    [onIconClick, onClose]
  );

  // Check if a window is currently open
  const isAppOpen = useCallback(
    (windowInstance: WindowInstance | undefined) => {
      if (!windowInstance) return false;
      return (
        isWindowOpen(windowInstance.state) ||
        isWindowMinimized(windowInstance.state)
      );
    },
    []
  );

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-[200] bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Spotlight container */}
      <div className="pointer-events-none fixed inset-0 z-[201] flex items-start justify-center pt-[15vh]">
        <div
          className={`pointer-events-auto mx-4 w-full max-w-[640px] overflow-hidden rounded-xl shadow-2xl ${
            isDark
              ? "border border-zinc-700/50 bg-zinc-900/95"
              : "border border-zinc-200/50 bg-white/95"
          } `}
          style={{
            backdropFilter: "blur(20px)",
            WebkitBackdropFilter: "blur(20px)",
          }}
        >
          {/* Search input */}
          <div
            className={`flex items-center gap-3 px-4 py-2 ${isDark ? "border-b border-zinc-700/50" : "border-b border-zinc-200/50"} `}
          >
            {/* Search icon */}
            <svg
              className={`h-5 w-5 flex-shrink-0 ${isDark ? "text-zinc-400" : "text-zinc-500"}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>

            <input
              ref={inputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Search apps..."
              className={`flex-1 bg-transparent text-base outline-none ${isDark ? "text-white placeholder-zinc-500" : "text-zinc-900 placeholder-zinc-400"} `}
            />

            {/* Keyboard shortcut hint */}
            <kbd
              className={`rounded px-2 py-0.5 text-xs font-medium ${isDark ? "bg-zinc-800 text-zinc-400" : "bg-zinc-100 text-zinc-500"} `}
            >
              ESC
            </kbd>
          </div>

          {/* Search results */}
          <div className="max-h-[400px] overflow-y-auto">
            {searchResults.length === 0 ? (
              <div
                className={`px-4 py-8 text-center ${isDark ? "text-zinc-500" : "text-zinc-400"} `}
              >
                No apps found
              </div>
            ) : (
              <div className="py-1">
                {searchResults.map((result, index) => {
                  const isSelected = index === selectedIndex;
                  const isOpen = isAppOpen(result.windowInstance);

                  return (
                    <button
                      key={result.icon.id}
                      onClick={() => handleResultClick(result)}
                      onMouseEnter={() => setSelectedIndex(index)}
                      className={`flex w-full items-center gap-3 px-4 py-1.5 text-left transition-colors duration-75 ${
                        isSelected
                          ? isDark
                            ? "bg-blue-500/20"
                            : "bg-blue-500/10"
                          : ""
                      } ${isDark ? "hover:bg-zinc-800/50" : "hover:bg-zinc-100/50"} `}
                    >
                      {/* App icon */}
                      <div
                        className={`flex h-8 w-8 items-center justify-center rounded-lg ${isDark ? "bg-zinc-800" : "bg-zinc-100"} `}
                      >
                        {typeof result.icon.icon === "string" ? (
                          <span className="text-2xl">{result.icon.icon}</span>
                        ) : (
                          <div className="flex h-6 w-6 items-center justify-center [&>*]:h-auto [&>*]:max-h-full [&>*]:w-auto [&>*]:max-w-full [&>img]:object-contain [&>svg]:h-full [&>svg]:w-full">
                            {result.icon.icon}
                          </div>
                        )}
                      </div>

                      {/* App name */}
                      <div className="min-w-0 flex-1">
                        <div
                          className={`truncate font-medium ${isDark ? "text-white" : "text-zinc-900"} `}
                        >
                          {result.icon.label}
                        </div>
                        {isOpen && (
                          <div
                            className={`text-xs ${isDark ? "text-zinc-500" : "text-zinc-400"} `}
                          >
                            Running
                          </div>
                        )}
                      </div>

                      {/* Open indicator */}
                      {isOpen && (
                        <div className="h-2 w-2 rounded-full bg-green-500" />
                      )}

                      {/* Enter hint for selected item */}
                      {isSelected && (
                        <kbd
                          className={`rounded px-1.5 py-0.5 text-xs font-medium ${isDark ? "bg-zinc-800 text-zinc-400" : "bg-zinc-200 text-zinc-500"} `}
                        >
                          ↵
                        </kbd>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
